import React, { useEffect, useRef, useState } from 'react';
import LogoPlaceholder from './LogoPlaceholder.tsx';

interface Client {
  name: string;
  logo?: string;
  isPlaceholder?: boolean;
  bgColor?: string;
}

const Clients: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const touchStartX = useRef<number>(0);
  const scrollStartPos = useRef<number>(0);
  
  const clients: Client[] = [
    { name: 'Discover Atlanta', logo: '/client-logos/discover-atlanta.svg' },
    { name: 'Cisco', logo: '/client-logos/1200px-Cisco_logo_blue_2016.svg.png' },
    { name: 'Dell', logo: '/client-logos/dell.png' },
    { name: 'John Deere', logo: '/client-logos/John_Deere_logo.png' },
    { name: 'NVIDIA', logo: '/portfolio-images/nvidia-logo-vert.png' },
    { name: 'National Park Service', logo: '/client-logos/nps.svg' },
    { name: 'Boise State', logo: '/client-logos/boise.png' },
    { name: 'University of Northern Iowa', logo: '/client-logos/uni-logo.png' },
    { name: 'DKG', logo: '/client-logos/logo-dkg2-e1650922587265.png' },
    { name: 'William Shatner', logo: '/client-logos/william-shatner.png' },
    { name: 'Osbourne Media', logo: '/client-logos/osbourne.png' },
    { name: 'Rootstock', logo: '/client-logos/rootstock.jpg' },
    { name: 'IGEL', logo: '/client-logos/Igel_Technology.png' },
    { name: 'AXIS Seed', logo: '/client-logos/axis-seed.png' },
    { name: 'Capra Bank', logo: '/client-logos/capra-logo.svg' },
    { name: 'McCullough Creative', logo: '/client-logos/mccullough.png' },
    { name: 'SAM', logo: '/client-logos/sam2.jpg' },
    { name: 'Traci Lords', logo: '/client-logos/traci-lords-logo3_500x.webp' },
    { name: 'Splendor Water', logo: '/client-logos/splendor-logo.png' },
    { name: 'Why Didn\'t I Think of That?', logo: '/client-logos/wditot.png' },
    { name: 'Hartig Drug', logo: '/client-logos/Hartig_Drug_logo.png' },
    { name: 'Silicon Beach', logo: '/client-logos/siliconbeach.png' },
  ];

  // Duplicate for seamless loop
  const allClients = [...clients, ...clients];

  // Intersection Observer to detect when component is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
        }
      },
      { threshold: 0.3 }
    );

    if (scrollRef.current) {
      observer.observe(scrollRef.current);
    }

    return () => {
      if (scrollRef.current) {
        observer.unobserve(scrollRef.current);
      }
    };
  }, [isInView]);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || !isInView) return;

    let scrollPosition = scrollContainer.scrollLeft || 0;
    const scrollSpeed = 0.67;
    let animationId: number;

    const animate = () => {
      if (!isTouching) {
        scrollPosition += scrollSpeed;

        // Reset scroll when reaching halfway (seamless loop)
        if (scrollPosition >= scrollContainer.scrollWidth / 2) {
          scrollPosition = 0;
        }

        scrollContainer.scrollLeft = scrollPosition;
      } else {
        // Sync position with manual scroll during touch
        scrollPosition = scrollContainer.scrollLeft;
      }
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [isInView, isTouching]);

  // Touch handlers for manual swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsTouching(true);
    touchStartX.current = e.touches[0].clientX;
    scrollStartPos.current = scrollRef.current?.scrollLeft || 0;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!scrollRef.current) return;
    const delta = touchStartX.current - e.touches[0].clientX;
    scrollRef.current.scrollLeft = scrollStartPos.current + delta;
  };

  const handleTouchEnd = () => {
    setIsTouching(false);
  };

  return (
    <div className="pt-24 pb-6 overflow-hidden">
      <h2 className="text-3xl md:text-4xl font-bold mb-12 md:mb-16 text-center text-primary-700 dark:text-gray-100">
        Clients
      </h2>
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-white dark:from-neutral-800 to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-white dark:from-neutral-800 to-transparent z-10 pointer-events-none" />
        
        <div
          ref={scrollRef}
          className="flex gap-4 sm:gap-6 md:gap-8 overflow-x-hidden py-4 px-4 sm:px-12 md:px-24"
          style={{ scrollBehavior: 'auto', WebkitOverflowScrolling: 'touch' }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {allClients.map((client, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-24 sm:w-32 md:w-48 h-16 sm:h-20 md:h-24"
            >
              {client.isPlaceholder ? (
                <LogoPlaceholder name={client.name} bgColor={client.bgColor} />
              ) : (
                <div className={`w-full h-full bg-white dark:bg-white rounded-lg flex items-center justify-center ${client.name === 'National Park Service' ? 'p-0' : 'p-2 sm:p-3 md:p-4'}`}>
                  <img
                    src={client.logo}
                    alt={`${client.name} logo`}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Clients;